import { InfoOutlined } from '@mui/icons-material';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncContent } from '~/components/AsyncContent';
import { SummaryItem } from '~/components/MyWorkspace/SummaryItem';
import { PublicEmployeeDialogContent } from '~/components/PublicEmployeeDialogContent';
import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { formatDuration } from '~/services/Formatters';

import { AddAbsentToday } from './components/AddAbsentToday';
import { WorktimeSignInOut } from './components/WorktimeSignInOut';
import { useMyDataWidgetContent } from './useMyDataWidgetContent';
import { useSxStyles } from './useStyles';

export const MyDataWidgetContent: React.FC = () => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    loading,
    failedToLoad,
    myEmployeeInfo,
    timeReportingIsEnabled,
    workTimeBalanceCalculationEnabled,
    selectedEmployeeId,
    openEmployeeProfileDialog,
    closeEmployeeProfileDialog,
    extraHoursBalance,
    availableBalances,
  } = useMyDataWidgetContent();

  return (
    <AsyncContent
      customSxStyles={{ root: sxStyles.root }}
      isLoading={loading}
      failedToLoad={failedToLoad}
    >
      <SummaryItem
        avatarProps={{
          alt: myEmployeeInfo.firstName,
          src: myEmployeeInfo.profilePic,
        }}
        listItemProps={{
          disableGutters: true,
        }}
        primaryElement={
          <Box sx={sxStyles.employeeMainData}>
            <Typography variant="inherit" noWrap={true}>
              {`${myEmployeeInfo.firstName} ${myEmployeeInfo.lastName}`}
            </Typography>
            <IconButton
              disableRipple={true}
              sx={sxStyles.infoIconButton}
              onClick={() => openEmployeeProfileDialog(myEmployeeInfo.id)}
              data-cy="open-employee-profile-dialog-icon-button"
              size="large"
            >
              <InfoOutlined />
            </IconButton>
          </Box>
        }
        secondaryElement={myEmployeeInfo.positionName.value}
      />

      <Box
        sx={{
          ...(!timeReportingIsEnabled && sxStyles.addAbsentTodayContainer),
        }}
      >
        <AddAbsentToday
          buttonText={t('oneDay')}
          secondButtonText={t('twoDays')}
          addOtherAbsencesButtonText={t('addOtherAbsences')}
          availableBalances={availableBalances}
        />
      </Box>

      {timeReportingIsEnabled && (
        <>
          <Typography
            variant="body2"
            sx={sxStyles.timeManagementTitle}
            data-cy="time-management-label"
          >
            <TooltippedTruncatedText>
              {t<string>('timeManagement')}
            </TooltippedTruncatedText>
          </Typography>

          {workTimeBalanceCalculationEnabled && (
            <Typography
              variant="body2"
              sx={sxStyles.myExtraHoursBalance}
              data-cy="my-extra-hours-balance-container"
            >
              <Box component="span" sx={sxStyles.myExtraHoursBalanceTitle}>
                <TooltippedTruncatedText>
                  {t<string>('myExtraHoursBalance')}
                </TooltippedTruncatedText>
              </Box>

              <span>
                {extraHoursBalance && formatDuration(extraHoursBalance)}
              </span>
            </Typography>
          )}

          <WorktimeSignInOut />
        </>
      )}

      <Dialog open={!!selectedEmployeeId} onClose={closeEmployeeProfileDialog}>
        {selectedEmployeeId && (
          <PublicEmployeeDialogContent employeeId={selectedEmployeeId} />
        )}
      </Dialog>
    </AsyncContent>
  );
};
